import React, { useCallback, useEffect, useRef, useState } from 'react';
import './productTabs.scss';

export type ProductTabSection = {
    id: string;
    label: string;
    /* Счётчик рядом с подписью, например число отзывов. Ноль не рисуем */
    badge?: number;
    content: React.ReactNode;
};

interface IProductTabsProps {
    sections: ProductTabSection[];
    isMobile: boolean;
    /* Секция, которую нужно раскрыть снаружи: переход по #reviews из личного кабинета */
    activeSectionId?: string;
    id?: string;
}

const renderLabel = (section: ProductTabSection) => (
    <>
        {section.label}
        {typeof section.badge === 'number' && section.badge > 0 && <span className="product-tabs__count">{section.badge}</span>}
    </>
);

const tabId = (sectionId: string) => `product-tab-${sectionId}`;
const panelId = (sectionId: string) => `product-panel-${sectionId}`;

const ProductTabs: React.FC<IProductTabsProps> = ({ sections, isMobile, activeSectionId, id }) => {
    const [activeTab, setActiveTab] = useState<string>(() => activeSectionId || (sections.length ? sections[0].id : ''));
    const [openSections, setOpenSections] = useState<string[]>(() => (sections.length ? [sections[0].id] : []));
    const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    /* Внешний выбор раскрывает секцию и в табах, и в аккордеоне */
    useEffect(() => {
        if (!activeSectionId) return;
        setActiveTab(activeSectionId);
        setOpenSections((prev) => (prev.includes(activeSectionId) ? prev : [...prev, activeSectionId]));
    }, [activeSectionId]);

    const toggleSection = useCallback((sectionId: string) => {
        setOpenSections((prev) => (prev.includes(sectionId) ? prev.filter((openId) => openId !== sectionId) : [...prev, sectionId]));
    }, []);

    if (!sections.length) return null;

    /* Выбранная секция могла исчезнуть: у товара выключили отзывы или очистили описание */
    const activeSection = sections.some((section) => section.id === activeTab) ? activeTab : sections[0].id;

    const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        const currentIndex = sections.findIndex((section) => section.id === activeSection);
        if (currentIndex === -1) return;

        let nextIndex: number | null = null;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % sections.length;
        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + sections.length) % sections.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = sections.length - 1;
        if (nextIndex === null) return;

        event.preventDefault();
        const nextSection = sections[nextIndex];
        setActiveTab(nextSection.id);
        tabRefs.current[nextSection.id]?.focus();
    };

    if (isMobile) {
        return (
            <div className="product-tabs product-tabs--accordion" id={id}>
                {sections.map((section) => {
                    const isOpen = openSections.includes(section.id);
                    return (
                        <div className="product-tabs__item" key={section.id}>
                            <button
                                type="button"
                                id={tabId(section.id)}
                                className="product-tabs__summary"
                                aria-expanded={isOpen}
                                aria-controls={panelId(section.id)}
                                onClick={() => toggleSection(section.id)}
                            >
                                <span className="product-tabs__label">{renderLabel(section)}</span>
                                <span className="product-tabs__chevron" aria-hidden="true" />
                            </button>
                            <div className="product-tabs__collapse" id={panelId(section.id)} role="region" aria-labelledby={tabId(section.id)}>
                                <div className="product-tabs__collapse-inner">
                                    <div className="product-tabs__content">{section.content}</div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <div className="product-tabs" id={id}>
            <div className="product-tabs__list" role="tablist" aria-label="Информация о товаре">
                {sections.map((section) => (
                    <button
                        key={section.id}
                        type="button"
                        role="tab"
                        id={tabId(section.id)}
                        className="product-tabs__tab"
                        aria-selected={section.id === activeSection}
                        aria-controls={panelId(section.id)}
                        tabIndex={section.id === activeSection ? 0 : -1}
                        ref={(node) => {
                            tabRefs.current[section.id] = node;
                        }}
                        onClick={() => setActiveTab(section.id)}
                        onKeyDown={handleTabKeyDown}
                    >
                        {renderLabel(section)}
                    </button>
                ))}
            </div>
            {sections.map((section) => (
                <div
                    key={section.id}
                    role="tabpanel"
                    id={panelId(section.id)}
                    className="product-tabs__panel"
                    aria-labelledby={tabId(section.id)}
                    hidden={section.id !== activeSection}
                >
                    <div className="product-tabs__content">{section.content}</div>
                </div>
            ))}
        </div>
    );
};

export default ProductTabs;
